import { useEffect, useMemo } from "react";

import { useQuery } from "@tanstack/react-query";

import { DocumentResponse } from "@/api";

interface Result {
  blobUrl: string | null;
  isLoading: boolean;
  isUnavailable: boolean;
}

export function useMergeRowPreview(
  file: DocumentResponse,
  fetchImageBlob: (file: DocumentResponse) => Promise<Blob>,
  skip = false
): Result {
  // React Query keys the fetch on file.documentId, so an unstable `fetchImageBlob` identity (callers
  // pass a fresh function each render) no longer re-triggers it — no "latest ref" needed.
  const {
    data: blob,
    isLoading: isQueryLoading,
    isError,
  } = useQuery({
    queryKey: ["merge-row-preview", file.documentId],
    queryFn: () => fetchImageBlob(file),
    enabled: !skip,
    staleTime: Infinity,
    retry: false,
  });

  // Derive a short-lived object URL from the blob and revoke it when it changes / unmounts.
  const blobUrl = useMemo(() => (blob ? URL.createObjectURL(blob) : null), [blob]);
  useEffect(
    () => () => {
      if (blobUrl) {
        URL.revokeObjectURL(blobUrl);
      }
    },
    [blobUrl]
  );

  return { blobUrl, isLoading: !skip && isQueryLoading, isUnavailable: isError };
}
